import { API_BASE } from '@/shared/services/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/shared/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/ui/table';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle,
  Database,
  Eye,
  GitBranch,
  Globe,
  LineChart,
  Link2,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Route,
  Send,
  Settings,
  ShieldCheck,
  Target,
  Wifi,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';


const statusStyles = {
  active: 'bg-green-100 text-green-700 border-green-200',
  healthy: 'bg-green-100 text-green-700 border-green-200',
  delivered: 'bg-green-100 text-green-700 border-green-200',
  monitoring: 'bg-blue-100 text-blue-700 border-blue-200',
  queued: 'bg-blue-100 text-blue-700 border-blue-200',
  partial_failure: 'bg-amber-100 text-amber-700 border-amber-200',
  needs_configuration: 'bg-amber-100 text-amber-700 border-amber-200',
  ready_for_credentials: 'bg-sky-100 text-sky-700 border-sky-200',
  credentials_required: 'bg-amber-100 text-amber-700 border-amber-200',
  disabled: 'bg-gray-100 text-gray-600 border-gray-200',
  failed: 'bg-red-100 text-red-700 border-red-200'
};

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value || 0);
const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value || 0);

function StatusBadge({ value }) {
  const label = String(value || 'unknown').replace(/_/g, ' ');
  return (
    <Badge variant="outline" className={statusStyles[value] || 'bg-gray-100 text-gray-600 border-gray-200'}>
      {label}
    </Badge>
  );
}

function MetricCard({ title, value, helper, icon: Icon, accent = 'text-teal-600', bg = 'bg-teal-50' }) {
  return (
    <Card className="border border-border shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bg}`}>
            <Icon className={`h-5 w-5 ${accent}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold leading-tight">{value}</p>
            {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function OrchestrationCenter() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({});
  const [sources, setSources] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [events, setEvents] = useState([]);
  const [roi, setRoi] = useState({});
  const [showRouteDialog, setShowRouteDialog] = useState(false);
  const [showDestinationDialog, setShowDestinationDialog] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [routeDraft, setRouteDraft] = useState({
    name: 'LinkedIn high-intent lead route',
    trigger: 'lead_created',
    campaign_scope: 'crm_campaigns',
    destinations: 'CRM Event Bus, LinkedIn CAPI',
    conditions: 'utm_source equals linkedin\nconsent_state equals granted',
    mapping: 'email -> user.email_hash\ncampaign_id -> conversion.campaign'
  });

  const fetchJson = useCallback(async (path) => {
    const response = await fetch(`${API_BASE}/api/orchestration${path}`);
    if (!response.ok) throw new Error(`Failed to load ${path}`);
    return response.json();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewData, sourcesData, routesData, destinationsData, eventsData, roiData] = await Promise.all([
        fetchJson('/overview'),
        fetchJson('/sources'),
        fetchJson('/routes'),
        fetchJson('/destinations'),
        fetchJson('/events'),
        fetchJson('/roi/overview')
      ]);
      setOverview(overviewData);
      setSources(sourcesData);
      setRoutes(routesData);
      setDestinations(destinationsData);
      setEvents(eventsData);
      setRoi(roiData);
    } catch (error) {
      toast.error(error.message || 'Failed to load orchestration data');
    } finally {
      setLoading(false);
    }
  }, [fetchJson]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const routePayloadPreview = useMemo(() => ({
    name: routeDraft.name,
    trigger: routeDraft.trigger,
    campaign_scope: routeDraft.campaign_scope,
    conditions: routeDraft.conditions.split('\n').filter(Boolean),
    destinations: routeDraft.destinations.split(',').map((item) => item.trim()).filter(Boolean),
    mappings: routeDraft.mapping.split('\n').filter(Boolean)
  }), [routeDraft]);

  const openEventDetail = async (event) => {
    setSelectedEvent(event);
    try {
      const detail = await fetchJson(`/events/${event.id}`);
      setEventDetail(detail);
    } catch (error) {
      setEventDetail(event);
      toast.error('Loaded event summary only');
    }
  };

  const testDestination = async () => {
    if (!selectedDestination) return;
    try {
      const response = await fetch(`${API_BASE}/api/orchestration/destinations/${selectedDestination.id}/test`, { method: 'POST' });
      const data = await response.json();
      toast.success(data.message || 'Destination dry-run completed');
      setShowDestinationDialog(false);
    } catch (error) {
      toast.error('Destination dry-run failed');
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="orchestration-center">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">Orchestration Center</h1>
          <p className="mt-1 text-muted-foreground">
            Route campaign, website, CRM, and ad-platform events from one native control plane.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => openEventDetail(events[0])}>
            <Play className="h-4 w-4" />
            Test Event
          </Button>
          <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={() => setShowRouteDialog(true)}>
            <Plus className="h-4 w-4" />
            New Route
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard title="Sessions Captured" value={formatNumber(overview.captured_sessions)} helper="Website, HERO, forms" icon={Globe} />
        <MetricCard title="Events Routed" value={formatNumber(overview.events_routed)} helper={`${overview.delivery_success_rate}% success`} icon={Route} accent="text-blue-600" bg="bg-blue-50" />
        <MetricCard title="Attributed Revenue" value={formatCurrency(overview.attributed_revenue)} helper={`ROAS ${overview.roas}x`} icon={LineChart} accent="text-emerald-600" bg="bg-emerald-50" />
        <MetricCard title="Failed Deliveries" value={formatNumber(overview.failed_deliveries)} helper={`${overview.active_destinations} active destinations`} icon={AlertTriangle} accent="text-amber-600" bg="bg-amber-50" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid h-auto w-full grid-cols-2 md:grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="intake">Intake</TabsTrigger>
          <TabsTrigger value="routes">Routes</TabsTrigger>
          <TabsTrigger value="destinations">Destinations</TabsTrigger>
          <TabsTrigger value="roi">ROI Sync</TabsTrigger>
          <TabsTrigger value="diagnostics">Diagnostics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-teal-600" /> Lifecycle Funnel</CardTitle>
                <CardDescription>Anonymous traffic through CRM revenue outcomes.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {(overview.funnel || []).map((stage, index) => (
                  <div key={stage.stage} className="flex items-center gap-3 rounded-lg border p-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-50 text-sm font-semibold text-teal-700">{index + 1}</div>
                    <div className="flex-1">
                      <p className="font-medium">{stage.stage}</p>
                      <div className="mt-2 h-2 rounded-full bg-muted">
                        <div className="h-2 rounded-full bg-teal-500" style={{ width: `${Math.max(8, Math.min(100, (stage.count / Math.max(overview.captured_sessions || 1, 1)) * 100))}%` }} />
                      </div>
                    </div>
                    <p className="text-xl font-bold">{formatNumber(stage.count)}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-blue-600" /> Architecture Lock</CardTitle>
                <CardDescription>Phase 1 foundation guardrails.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {['Native CRM surface', 'Source to route to destination model', 'Consent on every event', 'Dry-run before live delivery', 'Screenshot manifest coverage'].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>{item}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="intake">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5 text-teal-600" /> Intake Sources</CardTitle>
              <CardDescription>Website, HERO, tracking links, and CRM lifecycle events feeding the orchestration layer.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Signals</TableHead>
                    <TableHead className="text-right">Events Today</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sources.map((source) => (
                    <TableRow key={source.id}>
                      <TableCell className="font-medium">{source.name}</TableCell>
                      <TableCell>{source.type}</TableCell>
                      <TableCell><StatusBadge value={source.status} /></TableCell>
                      <TableCell className="space-x-1">{source.signals.map((signal) => <Badge key={signal} variant="secondary">{signal}</Badge>)}</TableCell>
                      <TableCell className="text-right font-semibold">{formatNumber(source.events_today)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="routes">
          <Card>
            <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><GitBranch className="h-5 w-5 text-teal-600" /> Routes</CardTitle>
                <CardDescription>Conditional routing from CRM and website signals into destinations.</CardDescription>
              </div>
              <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={() => setShowRouteDialog(true)}>
                <Plus className="h-4 w-4" /> New Route
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Route</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Conditions</TableHead>
                    <TableHead>Destinations</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Failures</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {routes.map((route) => (
                    <TableRow key={route.id}>
                      <TableCell className="font-medium">{route.name}</TableCell>
                      <TableCell><Badge variant="outline">{route.trigger}</Badge></TableCell>
                      <TableCell className="space-x-1">{route.conditions.map((condition) => <Badge key={condition} variant="secondary">{condition}</Badge>)}</TableCell>
                      <TableCell>{route.destinations.join(', ')}</TableCell>
                      <TableCell><StatusBadge value={route.status} /></TableCell>
                      <TableCell className="text-right font-semibold">{route.failures}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="destinations">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {destinations.map((destination) => (
              <Card key={destination.id}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Wifi className="h-5 w-5 text-teal-600" />
                        {destination.name}
                      </CardTitle>
                      <CardDescription>{destination.type}</CardDescription>
                    </div>
                    <StatusBadge value={destination.status} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm text-muted-foreground">Required fields</div>
                  <div className="flex flex-wrap gap-1">
                    {destination.required_fields.map((field) => <Badge key={field} variant="secondary">{field}</Badge>)}
                  </div>
                  <Button variant="outline" className="w-full gap-2" onClick={() => { setSelectedDestination(destination); setShowDestinationDialog(true); }}>
                    <Zap className="h-4 w-4" />
                    Test Destination
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="roi">
          <div className="grid gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-teal-600" /> ROI Summary</CardTitle>
                <CardDescription>Spend and CRM revenue reconciliation.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <MetricCard title="Spend" value={formatCurrency(roi.total_spend)} icon={Send} accent="text-orange-600" bg="bg-orange-50" />
                <MetricCard title="Revenue" value={formatCurrency(roi.attributed_revenue)} icon={LineChart} accent="text-emerald-600" bg="bg-emerald-50" />
                <MetricCard title="ROAS" value={`${roi.roas || 0}x`} icon={BarChart3} accent="text-blue-600" bg="bg-blue-50" />
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Campaign ROI</CardTitle>
                <CardDescription>Campaign-level spend and attributed revenue.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Campaign</TableHead>
                      <TableHead className="text-right">Spend</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">ROAS</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(roi.campaigns || []).map((campaign) => (
                      <TableRow key={campaign.campaign}>
                        <TableCell className="font-medium">{campaign.campaign}</TableCell>
                        <TableCell className="text-right">{formatCurrency(campaign.spend)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(campaign.revenue)}</TableCell>
                        <TableCell className="text-right font-semibold">{campaign.roas}x</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="diagnostics">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5 text-teal-600" /> Event Diagnostics</CardTitle>
              <CardDescription>Raw intake, normalized event, destination deliveries, and replay path.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Record</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell className="font-medium">{event.event_name}</TableCell>
                      <TableCell>{event.source}</TableCell>
                      <TableCell>{event.route}</TableCell>
                      <TableCell>{event.record}</TableCell>
                      <TableCell><StatusBadge value={event.status} /></TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" className="gap-2" onClick={() => openEventDetail(event)}>
                          <Eye className="h-4 w-4" />
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showRouteDialog} onOpenChange={setShowRouteDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Route</DialogTitle>
            <DialogDescription>Create a dry-run route contract before live destination delivery is enabled.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Route name</Label>
                <Input value={routeDraft.name} onChange={(event) => setRouteDraft({ ...routeDraft, name: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Trigger</Label>
                <Input value={routeDraft.trigger} onChange={(event) => setRouteDraft({ ...routeDraft, trigger: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Campaign scope</Label>
                <Input value={routeDraft.campaign_scope} onChange={(event) => setRouteDraft({ ...routeDraft, campaign_scope: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Destinations</Label>
                <Input value={routeDraft.destinations} onChange={(event) => setRouteDraft({ ...routeDraft, destinations: event.target.value })} />
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Conditions</Label>
                <Textarea className="min-h-[96px]" value={routeDraft.conditions} onChange={(event) => setRouteDraft({ ...routeDraft, conditions: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Mappings</Label>
                <Textarea className="min-h-[96px]" value={routeDraft.mapping} onChange={(event) => setRouteDraft({ ...routeDraft, mapping: event.target.value })} />
              </div>
            </div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium"><Settings className="h-4 w-4" /> Payload preview</div>
            <pre className="max-h-52 overflow-auto text-xs">{JSON.stringify(routePayloadPreview, null, 2)}</pre>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRouteDialog(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => { toast.success('Route contract staged for Phase 2 persistence'); setShowRouteDialog(false); }}>Save Route</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDestinationDialog} onOpenChange={setShowDestinationDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Test Destination</DialogTitle>
            <DialogDescription>Validate the destination payload contract without sending live external traffic.</DialogDescription>
          </DialogHeader>
          {selectedDestination && (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium">{selectedDestination.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedDestination.type}</p>
                </div>
                <StatusBadge value={selectedDestination.status} />
              </div>
              <div className="rounded-lg bg-muted p-3 text-sm">
                Live credentials are intentionally not required in Phase 1. This test checks payload shape, required fields, and replay readiness.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDestinationDialog(false)}>Cancel</Button>
            <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={testDestination}>
              <CheckCircle className="h-4 w-4" />
              Run Dry-Run Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={Boolean(selectedEvent)} onOpenChange={(open) => { if (!open) { setSelectedEvent(null); setEventDetail(null); } }}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Event Detail</SheetTitle>
            <SheetDescription>Route, payload, consent, and delivery diagnostics.</SheetDescription>
          </SheetHeader>
          {selectedEvent && (
            <div className="mt-6 space-y-4">
              <div className="rounded-lg border p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-lg font-semibold">{selectedEvent.event_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedEvent.record}</p>
                  </div>
                  <StatusBadge value={selectedEvent.status} />
                </div>
                <div className="mt-4 grid gap-3 text-sm md:grid-cols-2">
                  <div><span className="text-muted-foreground">Source:</span> {selectedEvent.source}</div>
                  <div><span className="text-muted-foreground">Route:</span> {selectedEvent.route}</div>
                  <div><span className="text-muted-foreground">Campaign:</span> {selectedEvent.campaign}</div>
                  <div><span className="text-muted-foreground">Consent:</span> {selectedEvent.consent_state}</div>
                </div>
              </div>

              <div className="rounded-lg border p-4">
                <p className="mb-3 font-medium">Deliveries</p>
                <div className="space-y-2">
                  {(selectedEvent.deliveries || []).map((delivery) => (
                    <div key={`${delivery.destination}-${delivery.status}`} className="flex items-center justify-between rounded-md bg-muted p-2 text-sm">
                      <span>{delivery.destination}</span>
                      <div className="flex items-center gap-2">
                        <StatusBadge value={delivery.status} />
                        <span className="text-muted-foreground">{delivery.attempts} attempts</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border p-4">
                <p className="mb-3 font-medium">Payloads</p>
                <div className="grid gap-3">
                  <div>
                    <p className="mb-1 text-sm text-muted-foreground">Raw intake</p>
                    <pre className="max-h-40 overflow-auto rounded-md bg-muted p-3 text-xs">{JSON.stringify(eventDetail?.raw_payload || {}, null, 2)}</pre>
                  </div>
                  <div>
                    <p className="mb-1 text-sm text-muted-foreground">Normalized event</p>
                    <pre className="max-h-40 overflow-auto rounded-md bg-muted p-3 text-xs">{JSON.stringify(eventDetail?.normalized_payload || {}, null, 2)}</pre>
                  </div>
                </div>
              </div>

              <Button className="w-full gap-2 bg-teal-600 hover:bg-teal-700" onClick={() => toast.success('Event queued for replay dry-run')}>
                <ArrowRight className="h-4 w-4" />
                Replay Dry-Run
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
